import { LoaderCircle, TriangleAlert, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { obtenerEstadoPago } from '../../utils/gastos'
import {
  formatearFechaPos,
  formatearMontoPos,
} from '../../utils/pos'

function Dato({ etiqueta, valor }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">
        {valor || '—'}
      </p>
    </div>
  )
}

function AnularVentaPosModal({
  abierto,
  venta,
  procesando = false,
  onCancelar,
  onConfirmar,
}) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (abierto) {
      setMotivo('')
      setError('')
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !procesando) {
        onCancelar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, procesando, onCancelar])

  if (!abierto || !venta) return null

  const estadoPago = obtenerEstadoPago(venta.estado_pago)

  const alHacerClicFuera = (event) => {
    if (procesando) return

    if (event.target === event.currentTarget) {
      onCancelar?.()
    }
  }

  const confirmar = () => {
    const motivoLimpio = motivo.trim()

    if (!motivoLimpio) {
      setError('Debes indicar el motivo de la eliminación.')
      return
    }

    setError('')
    onConfirmar(motivoLimpio)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="eliminar-venta-pos-titulo"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <TriangleAlert size={22} />
            </div>

            <h2
              id="eliminar-venta-pos-titulo"
              className="text-lg font-bold text-slate-900"
            >
              Eliminar venta
            </h2>
          </div>

          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          Esta venta será eliminada del listado operativo, pero se
          conservará su historial para auditoría.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <Dato
            etiqueta="Cliente"
            valor={venta.cliente_razon_social || 'Venta sin cliente'}
          />

          <Dato
            etiqueta="Fecha"
            valor={formatearFechaPos(venta.fecha_venta)}
          />

          <Dato
            etiqueta="Total venta"
            valor={formatearMontoPos(venta.total_venta)}
          />

          <Dato
            etiqueta="Estado pago"
            valor={estadoPago.etiqueta}
          />
        </div>

        <div className="mt-5">
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            Motivo de eliminación
            <span className="ml-1 text-red-500">*</span>
          </label>

          <textarea
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
            rows={3}
            placeholder="Ejemplo: Venta ingresada por error"
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
          />

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={procesando}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-3 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Eliminando...' : 'Eliminar venta'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AnularVentaPosModal
